import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  ApiKeysError,
  rotateApiKey,
  type ApiKeyListItem,
  type ApiKeysErrorReason,
} from '@/lib/api_keys';

import { mapApiKeysErrorReason } from './error_messages';
import { Input } from '@/components/ui';

interface ApiKeyRotateDialogProps {
  item: ApiKeyListItem;
  onSuccess(item: ApiKeyListItem): void;
  onCancel(): void;
}

export function ApiKeyRotateDialog({ item, onSuccess, onCancel }: ApiKeyRotateDialogProps) {
  const apiClient = useApiClient();
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<ApiKeysErrorReason | null>(null);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setSubmitting(true);
    setErrorReason(null);
    try {
      const updated = await rotateApiKey(apiClient, item.id, value);
      onSuccess(updated);
    } catch (err) {
      const reason: ApiKeysErrorReason = err instanceof ApiKeysError ? err.reason : 'unknown';
      setErrorReason(reason);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !submitting && onCancel()}>
      <DialogContent
        data-testid="api-key-rotate-dialog"
        aria-describedby={errorReason !== null ? 'api-key-rotate-error' : undefined}
        hideCloseButton
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{ptBR.apiKeys.rotateDialogTitle}</DialogTitle>
            <DialogDescription>{ptBR.apiKeys.rotateDialogHint}</DialogDescription>
          </DialogHeader>

          <dl className="mt-4 grid grid-cols-[6rem_1fr] gap-y-1 text-body-sm">
            <dt className="text-fg-muted">{ptBR.apiKeys.itemLast4Label}</dt>
            <dd className="font-mono text-fg" data-testid="api-key-rotate-current-last4">
              ••••{item.last4}
            </dd>
          </dl>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-body-sm text-fg">{ptBR.apiKeys.rotateDialogValueLabel}</span>
            <Input
              type="password"
              required
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={ptBR.apiKeys.rotateDialogValuePlaceholder}
              disabled={submitting}
              data-testid="api-key-rotate-value-input"
            />
          </label>

          {errorReason !== null ? (
            <p
              id="api-key-rotate-error"
              role="alert"
              data-testid="api-key-rotate-error"
              className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
            >
              {mapApiKeysErrorReason(errorReason)}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
              data-testid="api-key-rotate-cancel"
            >
              {ptBR.apiKeys.rotateDialogCancel}
            </Button>
            <Button
              type="submit"
              disabled={submitting || value.length === 0}
              data-testid="api-key-rotate-confirm"
            >
              {submitting ? ptBR.apiKeys.saving : ptBR.apiKeys.rotateDialogConfirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
